/**
 * SEP 5.c — USDC exact approve + mandatory forced re-quote.
 *
 * Boundary contract:
 *   - ONLY write on-chain call authorized in 5.c is `USDC.approve(spender, amount)`.
 *   - `amount` is the CURRENT successful sellable quote's total_price_microusdc,
 *     NEVER `quote - currentAllowance`, NEVER MaxUint256 / type(uint256).max,
 *     NEVER any locally-recomputed price.
 *   - `spender` is `config.sale_contract_address` at write time (runtime authority).
 *   - `token`   is `config.payment_token`         at write time (runtime authority).
 *   - `chainId` is `config.chain_id`              at write time (runtime authority).
 *   - `owner`   is the CURRENT connected wallet address.
 *   - No approve on mount / on connect / on quote / in useEffect. ONLY on
 *     explicit user click.
 *   - Post-confirm the caller MUST invalidate allowance + force a fresh
 *     /sale/quote for the CURRENT committed selection (see §L/§M/§T).
 *
 * SPLIT (testability):
 *   - `deriveApproveEligibility()`  — pure predicate + snapshot builder.
 *   - `ApproveController`           — pure state machine with injected deps.
 *   - `useUsdcApprove()`            — thin wagmi/viem wiring around the controller.
 *   - `shouldApplyApproveConfirmation()` — pure guard for context-drift.
 *   - `ERC20_APPROVE_ABI`           — minimal write-side ABI, approve() only.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { useWriteContract, usePublicClient } from "wagmi";
import type { Abi } from "viem";

import type { GridSelection } from "./grid-selection";
import { selectionsEqual } from "./grid-selection";
import type { FinancialReadContext } from "./usdc-reads";
import type { FinancialReadiness } from "./financial-readiness";
import type { SaleQuote } from "./sale-types";

// ---------------------------------------------------------------------
// Minimal ERC20 WRITE ABI — approve() ONLY.
// No transfer, transferFrom, permit, Permit2, etc. (spec §G).
// ---------------------------------------------------------------------
export const ERC20_APPROVE_ABI = [
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
] as const satisfies Abi;

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------
export interface ApproveSnapshot {
  chainId: number;
  owner: `0x${string}`;
  token: `0x${string}`;
  spender: `0x${string}`;
  /** EXACT quote total_price_microusdc, bigint, > 0. */
  amount: bigint;
  /** Committed rectangle for which the approve was authorized. */
  selection: GridSelection;
}

export type ApproveState =
  | { kind: "idle" }
  | { kind: "awaiting_wallet"; snapshot: ApproveSnapshot }
  | { kind: "submitted"; snapshot: ApproveSnapshot; txHash: `0x${string}` }
  | { kind: "confirming"; snapshot: ApproveSnapshot; txHash: `0x${string}` }
  | { kind: "confirmed"; snapshot: ApproveSnapshot; txHash: `0x${string}` }
  | { kind: "rejected"; snapshot: ApproveSnapshot }
  | { kind: "reverted"; snapshot: ApproveSnapshot; txHash: `0x${string}` }
  | {
      kind: "error";
      snapshot: ApproveSnapshot;
      error: string;
      txHash?: `0x${string}`;
    };

export type ApproveStateKind = ApproveState["kind"];

// ---------------------------------------------------------------------
// Eligibility gate (pure)
// ---------------------------------------------------------------------
export interface ApproveEligibilityInputs {
  readCtx: FinancialReadContext | null;
  quote: SaleQuote | null;
  readiness: FinancialReadiness | null;
  selection: GridSelection | null;
}

export type ApproveIneligibilityReason =
  | "read_context_missing"
  | "no_selection"
  | "no_quote"
  | "quote_not_v2"
  | "quote_not_sellable"
  | "quote_price_missing"
  | "readiness_missing"
  | "readiness_ready_for_purchase"
  | "readiness_insufficient_balance"
  | "amount_mismatch";

export type ApproveEligibility =
  | { eligible: true; snapshot: ApproveSnapshot }
  | { eligible: false; reason: ApproveIneligibilityReason };

/**
 * §E — approve is authorized IFF ALL of these hold:
 *   - read context is valid (implies config v2 + status ok + not paused +
 *     wallet connected + correct+supported chain + valid account)
 *   - a committed selection exists
 *   - a v2 quote exists, is `sellable`, and has a positive total price
 *   - financial readiness is `approval_required`
 *   - the derived approval amount equals the quote total (bigint identity)
 */
export function deriveApproveEligibility(
  i: ApproveEligibilityInputs,
): ApproveEligibility {
  if (i.readCtx == null) {
    return { eligible: false, reason: "read_context_missing" };
  }
  if (i.selection == null) return { eligible: false, reason: "no_selection" };
  if (i.quote == null) return { eligible: false, reason: "no_quote" };
  if (i.quote.generation !== "v2") {
    return { eligible: false, reason: "quote_not_v2" };
  }
  if (!i.quote.sellable) {
    return { eligible: false, reason: "quote_not_sellable" };
  }
  const total = i.quote.total_price_microusdc;
  if (total == null || total <= 0n) {
    return { eligible: false, reason: "quote_price_missing" };
  }
  if (i.readiness == null) {
    return { eligible: false, reason: "readiness_missing" };
  }
  if (i.readiness.kind === "ready_for_purchase") {
    return { eligible: false, reason: "readiness_ready_for_purchase" };
  }
  if (i.readiness.kind === "insufficient_balance") {
    return { eligible: false, reason: "readiness_insufficient_balance" };
  }
  // readiness.kind === "approval_required" — sanity check the amount.
  if (i.readiness.required_approval !== total) {
    return { eligible: false, reason: "amount_mismatch" };
  }
  return {
    eligible: true,
    snapshot: {
      chainId: i.readCtx.chainId,
      owner: i.readCtx.owner,
      token: i.readCtx.token,
      spender: i.readCtx.spender,
      amount: total,
      selection: { ...i.selection },
    },
  };
}

// ---------------------------------------------------------------------
// User rejection classifier (§J) — same semantics as chain-switch.
// ---------------------------------------------------------------------
export function isUserRejection(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    name?: string;
    code?: number | string;
    message?: string;
    shortMessage?: string;
    cause?: { name?: string; code?: number | string };
  };
  if (e.code === 4001 || e.code === "ACTION_REJECTED") return true;
  if (typeof e.name === "string" && /UserRejectedRequestError/i.test(e.name)) {
    return true;
  }
  if (e.cause) {
    if (e.cause.code === 4001 || e.cause.code === "ACTION_REJECTED") return true;
    if (typeof e.cause.name === "string" && /UserRejectedRequestError/i.test(e.cause.name)) {
      return true;
    }
  }
  const msg = String(e.message ?? "") + " " + String(e.shortMessage ?? "");
  if (/user rejected|user denied|rejected by user/i.test(msg)) return true;
  return false;
}

// ---------------------------------------------------------------------
// Controller — pure state machine driven by injected deps.
// ---------------------------------------------------------------------
export interface WriteContractParams {
  address: `0x${string}`;
  abi: typeof ERC20_APPROVE_ABI;
  functionName: "approve";
  args: readonly [`0x${string}`, bigint];
  chainId: number;
}

export interface ReceiptOutcome {
  status: "success" | "reverted";
}

export interface ApproveDeps {
  writeContract: (params: WriteContractParams) => Promise<`0x${string}`>;
  waitForReceipt: (hash: `0x${string}`) => Promise<ReceiptOutcome>;
  onStateChange?: (state: ApproveState) => void;
}

export class ApproveController {
  private _state: ApproveState = { kind: "idle" };

  constructor(private deps: ApproveDeps) {}

  get state(): ApproveState {
    return this._state;
  }

  /** True while a wallet or on-chain confirmation is in-flight. */
  get isPending(): boolean {
    return (
      this._state.kind === "awaiting_wallet" ||
      this._state.kind === "submitted" ||
      this._state.kind === "confirming"
    );
  }

  reset(): void {
    if (this.isPending) return;
    this.setState({ kind: "idle" });
  }

  private setState(next: ApproveState) {
    this._state = next;
    this.deps.onStateChange?.(next);
  }

  /**
   * The ONE public entry. Idempotent under double-click (§W): re-entry
   * while pending is silently ignored.
   *
   * The call sequence is:
   *   idle → awaiting_wallet
   *     → wallet returns hash → submitted → confirming
   *       → receipt success  → confirmed
   *       → receipt reverted → reverted
   *     → wallet throws (user rejection) → rejected
   *     → wallet throws (other)          → error
   */
  async approve(snap: ApproveSnapshot): Promise<void> {
    if (this.isPending) return;
    // Defensive: eligibility gate already rules this out, but the
    // controller MUST NOT send a zero/negative approval.
    if (typeof snap.amount !== "bigint" || snap.amount <= 0n) {
      this.setState({ kind: "error", snapshot: snap, error: "invalid_amount" });
      return;
    }

    this.setState({ kind: "awaiting_wallet", snapshot: snap });

    let hash: `0x${string}`;
    try {
      hash = await this.deps.writeContract({
        address: snap.token,
        abi: ERC20_APPROVE_ABI,
        functionName: "approve",
        args: [snap.spender, snap.amount] as const,
        chainId: snap.chainId,
      });
    } catch (err) {
      if (isUserRejection(err)) {
        this.setState({ kind: "rejected", snapshot: snap });
      } else {
        this.setState({
          kind: "error",
          snapshot: snap,
          error: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }

    this.setState({ kind: "submitted", snapshot: snap, txHash: hash });
    this.setState({ kind: "confirming", snapshot: snap, txHash: hash });

    try {
      const receipt = await this.deps.waitForReceipt(hash);
      if (receipt.status === "success") {
        this.setState({ kind: "confirmed", snapshot: snap, txHash: hash });
      } else {
        this.setState({ kind: "reverted", snapshot: snap, txHash: hash });
      }
    } catch (err) {
      this.setState({
        kind: "error",
        snapshot: snap,
        txHash: hash,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

// ---------------------------------------------------------------------
// Context-drift guard (§P/§Q/§R/§S) — decide whether to apply the
// consequences of a `confirmed` approve to the CURRENT UI context.
// ---------------------------------------------------------------------
export interface CurrentApproveContext {
  chainId: number;
  owner: `0x${string}`;
  token: `0x${string}`;
  spender: `0x${string}`;
  selection: GridSelection | null;
}

export function shouldApplyApproveConfirmation(
  approveState: ApproveState,
  current: CurrentApproveContext,
): boolean {
  if (approveState.kind !== "confirmed") return false;
  const s = approveState.snapshot;
  if (s.chainId !== current.chainId) return false;
  if (s.owner.toLowerCase() !== current.owner.toLowerCase()) return false;
  if (s.token.toLowerCase() !== current.token.toLowerCase()) return false;
  if (s.spender.toLowerCase() !== current.spender.toLowerCase()) return false;
  if (current.selection == null) return false;
  if (!selectionsEqual(s.selection, current.selection)) return false;
  return true;
}

// ---------------------------------------------------------------------
// React hook — thin wiring around ApproveController.
// ---------------------------------------------------------------------
export interface UseUsdcApproveResult {
  state: ApproveState;
  approve: (snap: ApproveSnapshot) => Promise<void>;
  reset: () => void;
  isPending: boolean;
}

export function useUsdcApprove(): UseUsdcApproveResult {
  const [state, setState] = useState<ApproveState>({ kind: "idle" });
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  // Deps ref is mutated on every render so the controller — which is
  // created ONCE — always calls into the freshest wagmi/viem primitives.
  const depsRef = useRef<{
    writeContract: (p: WriteContractParams) => Promise<`0x${string}`>;
    waitForReceipt: (h: `0x${string}`) => Promise<ReceiptOutcome>;
  }>({
    writeContract: async () => {
      throw new Error("wallet_not_ready");
    },
    waitForReceipt: async () => {
      throw new Error("public_client_not_ready");
    },
  });
  depsRef.current.writeContract = async (p) => {
    const hash = (await writeContractAsync({
      address: p.address,
      abi: p.abi,
      functionName: p.functionName,
      args: p.args as unknown as readonly [`0x${string}`, bigint],
      chainId: p.chainId,
    } as never)) as `0x${string}`;
    return hash;
  };
  depsRef.current.waitForReceipt = async (hash) => {
    if (publicClient == null) throw new Error("public_client_not_ready");
    const rc = await publicClient.waitForTransactionReceipt({ hash });
    return { status: rc.status === "success" ? "success" : "reverted" };
  };

  const controllerRef = useRef<ApproveController | null>(null);
  if (controllerRef.current == null) {
    controllerRef.current = new ApproveController({
      writeContract: (p) => depsRef.current.writeContract(p),
      waitForReceipt: (h) => depsRef.current.waitForReceipt(h),
      onStateChange: (s) => setState(s),
    });
  }

  const approve = useCallback(
    (snap: ApproveSnapshot) => controllerRef.current!.approve(snap),
    [],
  );
  const reset = useCallback(() => controllerRef.current!.reset(), []);
  const isPending = useMemo(
    () =>
      state.kind === "awaiting_wallet" ||
      state.kind === "submitted" ||
      state.kind === "confirming",
    [state.kind],
  );
  return { state, approve, reset, isPending };
}
